if (!customElements.get('cc-facets-drawer')) {
  customElements.define(
    'cc-facets-drawer',
    class CemeteryFacetsDrawer extends HTMLElement {
      constructor() {
        super();
        this.onClick = this.onClick.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.closeTimer = null;
      }

      connectedCallback() {
        if (this.dataset.bound === 'true') return;
        this.dataset.bound = 'true';
        this.addEventListener('click', this.onClick);
      }

      disconnectedCallback() {
        document.removeEventListener('keydown', this.onKeyDown);
        window.clearTimeout(this.closeTimer);
      }

      get layer() {
        return this.querySelector('[data-cc-facets-layer]');
      }

      get drawer() {
        return this.querySelector('.cc-facets__drawer');
      }

      get opener() {
        return this.querySelector('[data-cc-facets-open]');
      }

      onClick(event) {
        if (event.target.closest('[data-cc-facets-open]')) {
          event.preventDefault();
          this.open();
          return;
        }

        if (event.target.closest('[data-cc-facets-close]')) {
          event.preventDefault();
          this.close();
        }
      }

      onKeyDown(event) {
        if (event.key === 'Escape') this.close();
      }

      open() {
        if (!this.layer || !this.drawer) return;
        window.clearTimeout(this.closeTimer);
        this.layer.hidden = false;
        this.opener?.setAttribute('aria-expanded', 'true');
        document.body.classList.add('cc-overlay-open');
        document.addEventListener('keydown', this.onKeyDown);
        requestAnimationFrame(() => {
          this.layer.classList.add('is-open');
          if (typeof trapFocus === 'function') {
            trapFocus(this.drawer, this.querySelector('[data-cc-facets-close]'));
          } else {
            this.drawer.focus();
          }
        });
      }

      close() {
        if (!this.layer || this.layer.hidden) return;
        this.layer.classList.remove('is-open');
        this.opener?.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('cc-overlay-open');
        document.removeEventListener('keydown', this.onKeyDown);
        if (typeof removeTrapFocus === 'function') removeTrapFocus(this.opener);
        this.closeTimer = window.setTimeout(() => {
          this.layer.hidden = true;
        }, 280);
      }
    }
  );
}

if (!customElements.get('cc-grid-view')) {
  customElements.define(
    'cc-grid-view',
    class CemeteryGridView extends HTMLElement {
      constructor() {
        super();
        this.onClick = this.onClick.bind(this);
        this.onBreakpointChange = this.onBreakpointChange.bind(this);
        this.onCollectionUpdated = this.onCollectionUpdated.bind(this);
        this.desktopQuery = window.matchMedia('(min-width: 750px)');
      }

      connectedCallback() {
        if (this.dataset.bound === 'true') return;
        this.dataset.bound = 'true';
        this.addEventListener('click', this.onClick);
        document.addEventListener('cc:collection-updated', this.onCollectionUpdated);
        this.desktopQuery.addEventListener('change', this.onBreakpointChange);
        this.applyView(this.readStoredView());
      }

      disconnectedCallback() {
        document.removeEventListener('cc:collection-updated', this.onCollectionUpdated);
        this.desktopQuery.removeEventListener('change', this.onBreakpointChange);
      }

      get storageKey() {
        return this.dataset.storageKey || 'cemeterycrypt-product-grid-view-v52';
      }

      get isDesktop() {
        return this.desktopQuery.matches;
      }

      get allowedViews() {
        return this.isDesktop ? ['2', '4', '6'] : ['1', '2', '3'];
      }

      get defaultView() {
        return this.isDesktop ? '4' : '2';
      }

      get breakpointStorageKey() {
        return `${this.storageKey}-${this.isDesktop ? 'desktop' : 'mobile'}`;
      }

      get fallbackDatasetKey() {
        return this.isDesktop ? 'desktopView' : 'mobileView';
      }

      readStoredView() {
        try {
          const storedView = window.localStorage.getItem(this.breakpointStorageKey);
          return this.allowedViews.includes(storedView) ? storedView : this.defaultView;
        } catch (error) {
          const fallbackView = this.dataset[this.fallbackDatasetKey];
          return this.allowedViews.includes(fallbackView) ? fallbackView : this.defaultView;
        }
      }

      onClick(event) {
        const button = event.target.closest('[data-grid-view]');
        if (!button || !this.contains(button)) return;
        const nextView = button.dataset.gridView;
        if (!this.allowedViews.includes(nextView)) return;
        try {
          window.localStorage.setItem(this.breakpointStorageKey, nextView);
        } catch (error) {
          this.dataset[this.fallbackDatasetKey] = nextView;
        }
        document.documentElement.dataset[this.isDesktop ? 'ccGridDesktopView' : 'ccGridMobileView'] = nextView;
        this.applyView(nextView);
      }

      onBreakpointChange() {
        this.applyView(this.readStoredView());
      }

      onCollectionUpdated() {
        this.applyView();
      }

      findGrid() {
        const root = document.getElementById('product-grid');
        if (!root) return null;
        if (root.matches('ul.product-grid')) return root;
        return root.querySelector('ul.product-grid');
      }

      applyView(forcedView) {
        const currentView = this.allowedViews.includes(forcedView)
          ? forcedView
          : this.allowedViews.includes(this.dataset.gridView)
            ? this.dataset.gridView
            : this.readStoredView();
        const grid = this.findGrid();
        if (!grid) return;
        this.dataset.gridView = currentView;
        grid.classList.add('cc-product-grid');
        ['1', '2', '3', '4', '6'].forEach((view) => grid.classList.remove(`cc-grid--${view}`));
        grid.classList.add(`cc-grid--${currentView}`);
        this.querySelectorAll('[data-grid-view]').forEach((button) => {
          button.setAttribute('aria-pressed', String(button.dataset.gridView === currentView));
        });
        const desktopSizes = {
          2: 'calc((100vw - 8rem) / 2)',
          4: 'calc((100vw - 10rem) / 4)',
          6: 'calc((100vw - 13rem) / 6)',
        };
        const mobileSizes = {
          1: 'calc(100vw - 3rem)',
          2: 'calc((100vw - 3rem) / 2)',
          3: 'calc((100vw - 4rem) / 3)',
        };
        const imageSizes = this.isDesktop ? desktopSizes[currentView] : mobileSizes[currentView];
        grid.querySelectorAll('.card__media img').forEach((image) => image.setAttribute('sizes', imageSizes));
      }
    }
  );
}

if (!customElements.get('cc-price-slider')) {
  customElements.define(
    'cc-price-slider',
    class CemeteryPriceSlider extends HTMLElement {
      constructor() {
        super();
        this.onSliderInput = this.onSliderInput.bind(this);
        this.onSliderChange = this.onSliderChange.bind(this);
        this.onFieldInput = this.onFieldInput.bind(this);
        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerEnd = this.onPointerEnd.bind(this);
      }

      connectedCallback() {
        if (this.dataset.bound === 'true') return;

        this.minSlider = this.querySelector('[data-cc-price-slider-min]');
        this.maxSlider = this.querySelector('[data-cc-price-slider-max]');
        const pricePanel = this.closest('.cc-facets__panel--price');
        const fields = pricePanel?.querySelectorAll('.cc-price-range__input');
        this.minField = fields?.[0];
        this.maxField = fields?.[1];

        if (!this.minSlider || !this.maxSlider || !this.minField || !this.maxField) return;

        this.dataset.bound = 'true';
        this.minSlider.addEventListener('input', this.onSliderInput);
        this.maxSlider.addEventListener('input', this.onSliderInput);
        this.minSlider.addEventListener('change', this.onSliderChange);
        this.maxSlider.addEventListener('change', this.onSliderChange);
        this.minField.addEventListener('input', this.onFieldInput);
        this.maxField.addEventListener('input', this.onFieldInput);
        this.minField.addEventListener('change', this.onFieldInput);
        this.maxField.addEventListener('change', this.onFieldInput);
        this.addEventListener('pointerdown', this.onPointerDown);
        this.addEventListener('pointermove', this.onPointerMove);
        this.addEventListener('pointerup', this.onPointerEnd);
        this.addEventListener('pointercancel', this.onPointerEnd);
        this.syncSlidersFromFields();
      }

      disconnectedCallback() {
        this.minSlider?.removeEventListener('input', this.onSliderInput);
        this.maxSlider?.removeEventListener('input', this.onSliderInput);
        this.minSlider?.removeEventListener('change', this.onSliderChange);
        this.maxSlider?.removeEventListener('change', this.onSliderChange);
        this.minField?.removeEventListener('input', this.onFieldInput);
        this.maxField?.removeEventListener('input', this.onFieldInput);
        this.minField?.removeEventListener('change', this.onFieldInput);
        this.maxField?.removeEventListener('change', this.onFieldInput);
        this.removeEventListener('pointerdown', this.onPointerDown);
        this.removeEventListener('pointermove', this.onPointerMove);
        this.removeEventListener('pointerup', this.onPointerEnd);
        this.removeEventListener('pointercancel', this.onPointerEnd);
        delete this.dataset.bound;
      }

      get ceiling() {
        return Number(this.maxSlider?.max) || 0;
      }

      parseValue(value, fallback) {
        const normalizedValue = String(value ?? '').replace(/,/g, '').trim();
        if (normalizedValue === '') return fallback;
        const number = Number(normalizedValue);
        return Number.isFinite(number) ? number : fallback;
      }

      clamp(value) {
        return Math.min(Math.max(value, 0), this.ceiling);
      }

      formatValue(value) {
        return Number(value.toFixed(2)).toString();
      }

      onSliderInput(event) {
        event.stopPropagation();
        this.updateFromSliders(event.currentTarget);
      }

      updateFromSliders(activeSlider) {
        let minimum = this.clamp(this.parseValue(this.minSlider.value, 0));
        let maximum = this.clamp(this.parseValue(this.maxSlider.value, this.ceiling));

        if (minimum > maximum) {
          if (activeSlider === this.minSlider) {
            minimum = maximum;
            this.minSlider.value = minimum;
          } else {
            maximum = minimum;
            this.maxSlider.value = maximum;
          }
        }

        this.minField.value = minimum <= 0 ? '' : this.formatValue(minimum);
        this.maxField.value = maximum >= this.ceiling ? '' : this.formatValue(maximum);
        this.updateTrack(minimum, maximum);
      }

      onSliderChange(event) {
        this.commitSlider(event.currentTarget);
      }

      commitSlider(slider) {
        const field = slider === this.minSlider ? this.minField : this.maxField;
        field.dispatchEvent(new Event('input', { bubbles: true }));
        field.dispatchEvent(new Event('change', { bubbles: false }));
      }

      onPointerDown(event) {
        if (event.pointerType === 'mouse' && event.button !== 0) return;

        event.preventDefault();
        const pointerValue = this.valueFromPointer(event.clientX);
        const minimum = this.clamp(this.parseValue(this.minSlider.value, 0));
        const maximum = this.clamp(this.parseValue(this.maxSlider.value, this.ceiling));

        if (minimum === maximum) {
          this.activeSlider = pointerValue <= minimum ? this.minSlider : this.maxSlider;
        } else {
          const minimumDistance = Math.abs(pointerValue - minimum);
          const maximumDistance = Math.abs(pointerValue - maximum);
          this.activeSlider = minimumDistance <= maximumDistance ? this.minSlider : this.maxSlider;
        }

        this.activePointerId = event.pointerId;
        try {
          this.activeSlider.focus({ preventScroll: true });
        } catch (_error) {
          this.activeSlider.focus();
        }

        if (this.setPointerCapture) {
          try {
            this.setPointerCapture(event.pointerId);
          } catch (_error) {
            // Pointer capture is optional; dragging still works while the pointer remains over the control.
          }
        }
        this.setSliderFromPointer(pointerValue);
      }

      onPointerMove(event) {
        if (event.pointerId !== this.activePointerId || !this.activeSlider) return;
        event.preventDefault();
        this.setSliderFromPointer(this.valueFromPointer(event.clientX));
      }

      onPointerEnd(event) {
        if (event.pointerId !== this.activePointerId || !this.activeSlider) return;
        event.preventDefault();

        if (event.type !== 'pointercancel') {
          this.setSliderFromPointer(this.valueFromPointer(event.clientX));
        }

        const completedSlider = this.activeSlider;
        this.activeSlider = null;
        this.activePointerId = null;

        if (this.releasePointerCapture) {
          try {
            this.releasePointerCapture(event.pointerId);
          } catch (_error) {
            // Pointer capture may already be released by the browser.
          }
        }

        this.commitSlider(completedSlider);
      }

      valueFromPointer(clientX) {
        const bounds = this.getBoundingClientRect();
        const thumbRadius = Math.min(8, bounds.width / 2);
        const trackStart = bounds.left + thumbRadius;
        const trackWidth = Math.max(bounds.width - thumbRadius * 2, 1);
        const ratio = Math.min(Math.max((clientX - trackStart) / trackWidth, 0), 1);
        const sliderMinimum = Number(this.minSlider.min) || 0;
        const sliderMaximum = Number(this.minSlider.max) || this.ceiling;
        const step = Number(this.minSlider.step) || 1;
        const rawValue = sliderMinimum + ratio * (sliderMaximum - sliderMinimum);
        const steppedValue = sliderMinimum + Math.round((rawValue - sliderMinimum) / step) * step;
        return this.clamp(steppedValue);
      }

      setSliderFromPointer(value) {
        const minimum = this.clamp(this.parseValue(this.minSlider.value, 0));
        const maximum = this.clamp(this.parseValue(this.maxSlider.value, this.ceiling));

        if (this.activeSlider === this.minSlider) {
          this.minSlider.value = Math.min(value, maximum);
        } else {
          this.maxSlider.value = Math.max(value, minimum);
        }

        this.updateFromSliders(this.activeSlider);
      }

      onFieldInput() {
        this.syncSlidersFromFields();
      }

      syncSlidersFromFields() {
        let minimum = this.clamp(this.parseValue(this.minField.value, 0));
        let maximum = this.clamp(this.parseValue(this.maxField.value, this.ceiling));

        if (minimum > maximum) {
          if (document.activeElement === this.maxField) {
            minimum = maximum;
          } else {
            maximum = minimum;
          }
        }

        this.minSlider.value = minimum;
        this.maxSlider.value = maximum;
        this.updateTrack(minimum, maximum);
      }

      updateTrack(minimum, maximum) {
        const divisor = this.ceiling || 1;
        this.style.setProperty('--cc-price-slider-min', `${(minimum / divisor) * 100}%`);
        this.style.setProperty('--cc-price-slider-max', `${(maximum / divisor) * 100}%`);
      }
    }
  );
}
